/* eslint-disable no-underscore-dangle */
/**
 * @typedef {import('./index.js').Store<State, Getters>} Store
 */
/**
 * @template T
 * @typedef {import('./index.js').GetterFunction<T, State, Getters>} GetterFunction<T>
 */
/**
 * @template T
 * @template R
 * @typedef {import('./index.js').ActionFunction<T, R, State, Getters>} ActionFunction<T,R>
 */
/**
 * @typedef {import('./cart/cart.js').CartLine} CartLine
 * @typedef {import('./cart/cart.js').Cart} Cart
 */
/**
 * @typedef {Object} QueryOptions
 * @property {string} hotelCode - Hotel SynXis code
 * @property {array<string>} roomCodes - Array of room SynXis codes
 * @property {string} rateCode - SynXis rate code
 * @property {string} roomName - Hotel name
 * @property {string} bedType - Bed type SynXis code
 * @property {string} bedName - Bed name
 * @property {number} adultGuestCount - Number of adult guests
 * @property {number} childGuestCount - Number of child guests
 * @property {array<{ addultGuestCount: number, childGuestCount: number }>} aditionalGuests - Number of child guests for each aditional room
 * @property {number} roomQuantity - Number of rooms in booking
 * @property {string} stayDateStart - Start date in dayFormat
 * @property {string} stayDateEnd - End date in dayFormat
 * @property {string} iataNumber - Travel agent number
 * @property {string} groupCode - Code for group discount
 * @property {string} promotionCode - Code for promotional discount
 */
/**
 * @typedef {object} DateRangeInfo
 * @property {string} startDay - First day of range in dateRangeFormat. ex. "5/1/2022"
 * @property {string} endDay - Second day of range in dateRangeFormat. ex. "5/31/2022"
 * @property {string} price - Price of each night during this range. ex. "50.00"
 * @property {string} priceWithTaxes - Price with taxes. ex. "83.25"
 * @property {string} status - Status of rooms availability during this range. ex. "Open" || "Close" || "MinStay"
 * @property {string} minStay - Minimum stay for this range
 */
/**
 * @typedef {object} CalendarDateRange
 * @property {string} startDay - First day of range in dateRangeFormat. ex. "5/1/2022"
 * @property {string} endDay - Second day of range in dateRangeFormat. ex. "5/31/2022"
 * @property {string} price - Price of each night during this range. ex. "50.00"
 * @property {string} priceWithTaxes - Price with taxes. ex. "83.25"
 * @property {string} status - Status of rooms availability during this range. ex. "Open" || "Close" || "MinStay"
 * @property {string} minStay - Minimum stay for this range
 * @property {string} hotelCode - Hotel code
 * @property {string} [promotionCode] - Code for promotional discount
 * @property {string} [roomCodes] - SynXis codes separated by ','
 * @property {string} [rateCode] - SynXis rate code
 */
/**
 * @typedef {object} GuestsParams
 * @property {boolean} capitalize - Capitalize the labels
 * @property {QueryOptions} customQuery
 */
/**
 * @typedef {object} CheckOutRequest
 * @property {string} cardHolderName
 * @property {string} cardNumber
 * @property {string} cardCode
 * @property {string} cardSeriesCode
 * @property {string} cardExpirateDate
 * @property {string} streetAddress
 * @property {string} city
 * @property {string} country
 * @property {string} email
 * @property {string} emailConfirmation
 * @property {string} firstName
 * @property {string} lastName
 * @property {string} phoneCountryCode
 * @property {string} phoneNumber
 * @property {string} zipCode
 * @property {string} state
 * @property {string} title
 * @property {string} reasonsToStay
 * @property {string} details
 * @property {Cart} cartModel
 * @property {string} userAgent
 */

import axios from 'axios'
import { capitalize, isEmptyOrNullOrUndefined } from '@utils/string-helpers'
import { getDate, useDuration } from '@utils/date-helpers'
import { logger } from '@plugins/logger'
import { getApiUrl } from '@utils/api-helpers'
/**
 * @module booking
 * @description
 * Handle all booking main logic, it also uses other store modules internally:
 * - [auth]{@link /docs/api/store/auth}
 * - [cart]{@link /docs/api/store/cart}
 * - [currency]{@link /docs/api/store/currency}
 * - [hotelData]{@link /docs/api/store/hoteldata}
 * - [i18n]{@link /docs/api/store/i18n}
 * - [rooms]{@link /docs/api/store/rooms}
 */
// Default Query
/** @type {QueryOptions} */
const defaultQuery = {
  hotelCode: null,
  roomCodes: [],
  roomName: null,
  bedType: null,
  rateCode: null,
  adultGuestCount: 2,
  childGuestCount: 0,
  roomQuantity: 1,
  aditionalGuests: [],
  stayDateStart: null,
  stayDateEnd: null,
  iataNumber: null,
  groupCode: null,
  promotionCode: null,
  jumpMonth: null,
  bookingEditMode: null,
}
// Steps list
const steps = ['room-list', 'join-fans', 'enhancements', 'check-out']
// Day formats
export const monthFormat = 'MM-YYYY'
export const dayFormat = 'YYYY-MM-DD'
export const hourFormat = 'HH:mm'
export const dateTimeFormat = `${dayFormat} ${hourFormat}`
export const dateRangeFormat = 'M[/]D[/]YYYY'
// Router variables
let router = null
let route = null

/**
 * Uses the step index to create the route object
 * keeping the current route query parameters.
 * @param {Number} step - Step index to use
 * @return {VueRouteObject}
 */
const genenerateRouteParams = (step) => ({
  name: steps[step],
  query: {
    ...route.query,
    step,
  },
})

/**
 * Compares each key and value from two objects.
 * Returns true if all obj1 keys and values are contained in obj2.
 * @param {Object} obj1 - Main object
 * @param {Object} obj2 - Comparison object
 * @return {Boolean}
 */
const compareObjects = (obj1, obj2) => {
  const obj1Keys = Object.keys(obj1)
  const obj2Keys = Object.keys(obj2)
  const obj1Values = Object.values(obj1)
  const obj2Values = Object.values(obj2)
  const differentKeys = obj1Keys.filter((k) => !obj2Keys.includes(k))
  const differentValues = obj1Values.filter((k) => !obj2Values.includes(k))
  return differentKeys.length === 0 && differentValues.length === 0
}

/**
 * Check if router and route have been registered
 * and uses router.push method.
 * @param {VueRouteObject} params
 * @param {'push'|'replace'} [method='push']
 */
const routerPush = (params, method = 'push') => (router && route ? router[method](params) : null)

/**
 * Updates the router query based on changes on the
 * store query.
 * @param {QueryOptions} query
 */
const routerQuery = (query) => {
  // Only try to update if router and route have been initiated and the queries are different
  if (router && route && !compareObjects(query, route.query)) {
    const transformedQuery = {}
    Object.keys(query).forEach((key) => {
      // If the property is an object we need to stringify it
      if (query[key] && typeof query[key] === 'object') {
        transformedQuery[key] = JSON.stringify(query[key])
      } else {
        transformedQuery[key] = query[key]
      }
    })
    // If the query have not changed do nothing
    if (
      JSON.stringify(route.query)
      === JSON.stringify({
        ...route.query,
        ...transformedQuery,
      })
    ) {
      return null
    }
    return router.replace({
      query: {
        ...route.query,
        ...transformedQuery,
      },
    })
  }
  return null
}

/**
 * Updates the store query based on changes on the
 * route query or a custom one.
 * @param {string} prop - Name of the prop
 * @param {string|array|object|number} value - Value to be updated
 * @param {QueryOptions} query - Store query
 * @param {QueryOptions} [customQuery] - Custom query
 */
const updateQueryProp = (prop, value, query, customQuery) => {
  const useQuery = customQuery || query
  // If the value is a string, trim it
  if (typeof value === 'string') {
    value.trim()
  }
  // roomCodes and aditionalGuests properties are arrays we need to parse them from the route query
  if (
    ['roomCodes', 'aditionalGuests'].includes(prop)
    && typeof value === 'string'
  ) {
    let array = null
    try {
      array = JSON.parse(value)
    } catch (e) {
      array = value === 'null' ? null : [value]
    }
    // If is a array of string, trim each value
    if (typeof array[0] === 'string') {
      query[prop] = array.map((a) => a.trim())
    } else {
      query[prop] = array
    }
    // If start date is after end date, update end date
  } else if (
    prop === 'stayDateStart'
    && getDate(value).isSameOrAfter(getDate(useQuery.stayDateEnd), 'day')
  ) {
    query.stayDateEnd = getDate(value).add(1, 'day').format(dayFormat)
    // If end date is before start date, update start date
  } else if (
    prop === 'stayDateEnd'
    && getDate(value).isSameOrBefore(getDate(useQuery.stayDateStart), 'day')
  ) {
    query.stayDateStart = getDate(value).subtract(1, 'day').format(dayFormat)
  } else {
    query[prop] = value === 'null' ? null : value
  }
}

/**
 * Check if a specifc day is contained in a dateRange
 * @param {String} day - Day using the dayFormat ('YYYY-MM-DD')
 * @param {CalendarDateRange} dateRange - Info returned by the server for a specific Range
 * @return {boolean}
 */
const checkDateRangeInfo = (day, dateRange) => {
  const startDateMatch = getDate(dateRange.startDay, dateRangeFormat).isSame(
    day,
    'day'
  )
  const endDateMatch = getDate(dateRange.endDay, dateRangeFormat).isSame(
    day,
    'day'
  )
  const innerDateMatch = getDate(dateRange.startDay, dateRangeFormat).isBefore(day, 'day')
    && getDate(dateRange.endDay, dateRangeFormat).isAfter(day, 'day')
  return startDateMatch || endDateMatch || innerDateMatch
}

/**
 * Check if the state.query date range is contained in a dateRante
 * @param {QueryOptions} query - State query property
 * @param {CalendarDateRange} dateRange - Info returned by the server for a specific Range
 * @return {boolean}
 */
const checkSelectedDaysRangeInfo = (query, dateRange) => {
  const startDateMatch = checkDateRangeInfo(query.stayDateStart, dateRange)
  const endDateMatch = checkDateRangeInfo(query.stayDateEnd, dateRange)
  const innerStartDateMatch = getDate(dateRange.startDay, dateRangeFormat).isAfter(
    query.stayDateStart,
    'day'
  )
    && getDate(dateRange.startDay, dateRangeFormat).isBefore(
      query.stayDateEnd,
      'day'
    )
  const innerEndDateMatch = getDate(dateRange.startDay, dateRangeFormat).isAfter(
    query.stayDateStart,
    'day'
  )
    && getDate(dateRange.startDay, dateRangeFormat).isBefore(
      query.stayDateEnd,
      'day'
    )
  return (
    startDateMatch || endDateMatch || innerStartDateMatch || innerEndDateMatch
  )
}

const filterByRoomType = (dates, query) => {
  if (query.bedType) {
    return dates.filter(
      (d) => d.roomCodes === query.bedType
    )
  }
  if (query.roomCodes && query.roomCodes.length > 0) {
    return dates.filter(
      (d) => d.roomCodes === query.roomCodes.join(',')
    )
  }
  return dates
}

const filterByRateCode = (dates, query) => {
  if (query.rateCode) {
    return dates.filter((d) => d.rateCode === query.rateCode)
  }
  return dates
}

const filterByPromotionCode = (dates, query) => dates.filter((d) => {
  if (isEmptyOrNullOrUndefined(d.promotionCode) && isEmptyOrNullOrUndefined(query.promotionCode)) {
    return true
  }
  return d.promotionCode === query.promotionCode
})
export default {
  namespaced: true,
  /** Booking State
   * @typedef {Object} State
   * @property {Boolean} ready - All data have been registered
   * @property {Number} step - Current step index
   * @property {MomentDate} now - Moment date object for now
   * @property {Number} currentRoomId - Id for the cart line beeing edited by the booking flow
   * @property {Array<DateRangeInfo>} calendarDates - Ranges returned by the server for the calendar
   * @property {Boolean} calendarLoading - Sets true when getting calendar availability
   * @property {String} nextStepLabel - Label for the footer button
   * @property {Number} nextStepIndex - Index for the next step in footer button
   * @property {String} nextStepClass - Class for the footer button
   * @property {Boolean} checkOutPending - Sets true when making the checkout call
   * @property {QueryOptions} query - Query options for the booking flow
   * @property {Number} rangeTotal - Total price of selected range in Calendar
   */
  /** @type {State} */
  state: {
    configured: false,
    ready: false,
    step: 0,
    now: getDate(),
    currentRoomId: 0,
    calendarDates: {},
    calendarLoading: null,
    nextStepLabel: null,
    nextStepIndex: null,
    nextStepClass: '',
    checkOutPending: false,
    query: {
      ...defaultQuery,
    },
    rangeTotal: 0.0,
  },
  /** Booking Getters
   * @typedef {Object} Getters
   * @property {GetterFunction<VueRoute>} getRoute - returns route object
   * @property {GetterFunction<string>} monthFormat - 'MM-YYYY'
   * @property {GetterFunction<string>} dayFormat - 'YYYY-MM-DD'
   * @property {GetterFunction<string>} hourFormat - 'HH:mm'
   * @property {GetterFunction<string>} dateTimeFormat - `${dayFormat} ${hourFormat}`
   * @property {GetterFunction<string>} dateRangeFormat - 'M[/]D[/]YYYY'
   * @property {GetterFunction<HotelData>} selectedHotel - Hotel data based on the hotelCode from the QueryOptions
   * @property {GetterFunction<Array<CalendarDateRange>>} hotelCalendarDates - Calendar rates based on the QueryOptions, filters based on roomCodes, rateCodes, promotionCode and hotelCode
   * @property {GetterFunction<boolean>} haveClosedDaySelected - Returns true if QueryOptions range contains unavailable dates
   * @property {GetterFunction<boolean>} havePastDaySelected - Returns true if stayDateStart is before today
   * @property {GetterFunction<boolean>} haveLowerMinStaySelected - Returns true if stayDateStart is before today
   * @property {GetterFunction<CartLine>} currentRoom - Current room info from the cart
   * @property {GetterFunction<{adultGuestCount: number, childGuestCount: number}>} currentRoomGuests - Current room info from the cart
   * @property {GetterFunction<QueryOptions>} getQuery - Get QueryOptions
   * @property {GetterFunction<DateRangeInfo>} getDateRangeInfo - Get the DateRangeInfo that matches a specific day
   * @property {GetterFunction<number>} getRangeInDays - Number of nights based on the QueryOptions
   * @property {GetterFunction<number>} getDateRangeInDays - Total price based on the QueryOptions
   * @property {GetterFunction<number>} getRangeTotal - Total price based on the QueryOptions
   * @property {GetterFunction<number>} getRangeAverage - Avarege price based on the QueryOptions
   * @property {GetterFunction<string>} getAdultDetails - Get number of adults display string
   * @property {GetterFunction<string>} getChildDetails - Get number of children display string
   * @property {GetterFunction<string>} getRoomDetails - Get number of rooms display string
   * @property {GetterFunction<string>} getSummaryDetails - Get all tree property strings (child, adult, room) in one summary
   * @property {GetterFunction<Object>} getBookingTracking - Get default data layer for booking events tracking
   * @property {GetterFunction<Object>} getBookingTrackingV2 - V2 Adobe Tracking Get default data layer for booking events tracking
   */
  /** @type {Getters} */
  getters: {
    getRoute: () => () => route,
    monthFormat: () => monthFormat,
    dayFormat: () => dayFormat,
    hourFormat: () => hourFormat,
    dateTimeFormat: () => dateTimeFormat,
    dateRangeFormat: () => dateRangeFormat,
    isReady: (state) => state.ready,
    showPricesWithTaxes: (state, getters, rootState, rootGetters) => (localQuery = null) => {
      const query = localQuery ?? state.query
      const { isChile, isCalifornia, propertiesWithTax } = rootState.global
      const propertySelectedIsTaxed = (
        query.hotelCode && propertiesWithTax.includes(query.hotelCode)
      )

      return isCalifornia || (propertySelectedIsTaxed && isChile)
    },
    selectedHotel: (state, getters, rootState, rootGetters) => rootGetters['hotelData/getHotelById'](state.query.hotelCode),
    hotelCalendarDates: (state) => (/** @type {QueryOptions} */ customQuery, month) => {
      const query = customQuery ?? state.query
      if (
        !state.calendarDates[query.hotelCode]
        || (month && !state.calendarDates[query.hotelCode][month])
      ) {
        return []
      }

      let filteredDates = month
        ? state.calendarDates[query.hotelCode]?.[month]
        : Object.values(state.calendarDates[query.hotelCode]).flatMap((r) => r)

      filteredDates = filterByRoomType(filteredDates, query)
      filteredDates = filterByRateCode(filteredDates, query)
      filteredDates = filterByPromotionCode(filteredDates, query)

      return filteredDates
    },
    haveClosedDaySelected:
      (state, getters) => (/** @type {QueryOptions} */ customQuery) => {
        const query = customQuery ?? state.query
        if (!query.stayDateStart || !query.stayDateEnd) {
          return false
        }
        const selectedRangeInfo = getters
          .hotelCalendarDates(query)
          .filter((dateRange) => checkSelectedDaysRangeInfo(query, dateRange))
        const closedDaysSelected = selectedRangeInfo.filter(
          (dateRange) => dateRange.status === 'Close'
        )
        const isClosedDayCheckOut = closedDaysSelected.length === 1
          && getDate(closedDaysSelected[0].startDay, dateRangeFormat).isSame(
            getDate(query.stayDateEnd, dayFormat),
            'day'
          )
        return closedDaysSelected.length > 0 && !isClosedDayCheckOut
      },
    havePastDaySelected:
      (state, getters) => (/** @type {QueryOptions} */ customQuery) => {
        const query = customQuery ?? state.query
        return getDate(query.stayDateStart).isBefore(state.now, 'day')
      },
    haveLowerMinStaySelected:
      (state, getters) => (/** @type {QueryOptions} */ customQuery) => {
        const query = customQuery ?? state.query
        if (!query.stayDateStart) {
          return false
        }
        const month = getDate(query.stayDateStart).format(monthFormat)
        // Get only the check in date as a parameter
        const selectedRangeInfo = getters
          .hotelCalendarDates(query, month)
          .filter((dateRange) => checkSelectedDaysRangeInfo(
            {
              ...query,
              stayDateEnd: query.stayDateStart,
            },
            dateRange
          ))
        const minStaySelected = selectedRangeInfo.filter(
          (dateRange) => dateRange.status === 'MinStay'
        )
        if (
          minStaySelected.length === 0
          || !query.stayDateStart
          || !query.stayDateEnd
        ) {
          return false
        }
        const selectedDaysRange = getters.getDateRangeInDays(query)
        const minStay = Number(minStaySelected[0].minStay)
        const isLowerThanMinStay = selectedDaysRange < minStay
        return isLowerThanMinStay && minStay
      },
    currentRoom: (state, getters, rootState, rootGetters) => rootGetters['cart/itemsInCart'][state.currentRoomId],
    currentRoomIsLastRoom: (state, getters, rootState, rootGetters) => state.currentRoomId === rootGetters['cart/itemsInCart'].length - 1,
    currentRoomGuests: (state, getters, rootState, rootGetters) => (state.currentRoomId === 0
      ? {
        adultGuestCount: state.query.adultGuestCount,
        childGuestCount: state.query.childGuestCount,
      }
      : {
        adultGuestCount:
          state.query.aditionalGuests[state.currentRoomId - 1]
            .adultGuestCount,
        childGuestCount:
          state.query.aditionalGuests[state.currentRoomId - 1]
            .childGuestCount,
      }),
    getStep: (state) => state.step,
    getQuery: (state) => (state.currentRoomId === 0
      ? state.query
      : {
        ...state.query,
        adultGuestCount:
          state.query.aditionalGuests[state.currentRoomId - 1]
            .adultGuestCount,
        childGuestCount:
          state.query.aditionalGuests[state.currentRoomId - 1]
            .childGuestCount,
      }),
    getDateRangeInfo:
      (state, getters) => (day, /** @type {QueryOptions} */ customQuery) => {
        const query = customQuery ?? state.query
        const month = getDate(day).format(monthFormat)
        const dayRangeInfo = getters
          .hotelCalendarDates(query, month)
          .filter((dateRange) => checkDateRangeInfo(day, dateRange))
        return dayRangeInfo[0]
      },
    getRangeInDays: (state, getters) => getters.getDateRangeInDays(state.query),
    getDateRangeInDays:
      (state) => (/** @type {QueryOptions} */ customQuery) => {
        const query = customQuery ?? state.query
        if (!query.stayDateStart || !query.stayDateEnd) {
          return 0
        }
        const startDate = getDate(query.stayDateStart, dayFormat)
        const endDate = getDate(query.stayDateEnd, dayFormat)
        return Math.round(useDuration(endDate.diff(startDate)).asDays())
      },
    getRangeTotal: (state, getters) => () => state.rangeTotal,
    getRangeAverage:
      (state, getters) => (/** @type {QueryOptions} */ customQuery) => {
        const duration = getters.getDateRangeInDays(customQuery)
        if (duration === 0) {
          return 0
        }
        return state.rangeTotal / duration || 0.0
      },
    getAdultDetails:
      (state, getters, rootState, rootGetters) => (
        /** @type {GuestsParams} */
        { capitalized = false, customQuery, displayLabel = true } = {}
      ) => {
        const tc = rootGetters['i18n/tc']
        const query = customQuery ?? state.query
        const aditionalAdults = query.aditionalGuests
          ? query.aditionalGuests
            .map((g) => Number(g.adultGuestCount))
            .reduce((a, b) => a + b, 0)
          : 0
        const count = Number(query.adultGuestCount) + aditionalAdults
        if (!count) {
          return false
        }
        let label = count > 1 ? tc('noums.adultPlural') : tc('noums.adultSingular')
        if (capitalized) {
          label = capitalize(label)
        }
        if (!displayLabel) return `<span class="adult">${count}</span>`
        return `${count} ${label}`
      },
    getChildDetails:
      (state, getters, rootState, rootGetters) => (
        /** @type {GuestsParams} */
        { capitalized = false, customQuery, displayLabel = true } = {}
      ) => {
        const tc = rootGetters['i18n/tc']
        const query = customQuery ?? state.query
        const aditionalChild = query.aditionalGuests
          ? query.aditionalGuests
            .map((g) => Number(g.childGuestCount))
            .reduce((a = 0, b = 0) => a + b, 0)
          : 0
        const count = Number(query.childGuestCount) + aditionalChild
        if (!count) {
          return false
        }
        let label = count > 1 ? tc('noums.childPlural') : tc('noums.childSingular')
        if (capitalized) {
          label = capitalize(label)
        }
        if (!displayLabel) return `<span class="child">${count}</span>`
        return `${count} ${label}`
      },
    getRoomDetails:
      (state, getters, rootState, rootGetters) => (
        /** @type {GuestsParams} */
        { capitalized = false, customQuery, displayLabel = true } = {}
      ) => {
        const tc = rootGetters['i18n/tc']
        const query = customQuery ?? state.query
        const count = parseInt(query.roomQuantity)
        if (!count) {
          return false
        }
        let label = count > 1 ? tc('noums.roomPlural') : tc('noums.roomSingular')
        if (capitalized) {
          label = capitalize(label)
        }
        if (!displayLabel) return `<span class="room">${count}</span>`
        return `${count} ${label}`
      },
    getSummaryDetails:
      (state, getters) => (
        /** @type {GuestsParams} */
        {
          capitalized = false,
          separator = ', ',
          customQuery,
          displayLabel = true,
        } = {}
      ) => {
        const items = [
          getters.getAdultDetails({ capitalized, customQuery, displayLabel }),
          getters.getChildDetails({ capitalized, customQuery, displayLabel }),
          getters.getRoomDetails({ capitalized, customQuery, displayLabel }),
        ].filter((i) => typeof i === 'string')
        return items.join(separator)
      },
    getBookingTracking:
      (state, getters, rootState, rootGetters) => (query = state.query, events = {}) => {
        const userProfile = rootState.auth.user
        let userParams = {}
        if (userProfile) {
          userParams = {
            user: {
              accountType: rootGetters['auth/fanLevel'],
              accountNumber: rootGetters['auth/userId']
                ? rootGetters['auth/userId'].toLowerCase()
                : '',
              loginState: 'logged in',
              ireckonuID: userProfile?.personId?.toString() || null
            },
          }
        }
        return {
          events: {
            ...events,
          },
          page: {
            hotelCode: query.hotelCode,
            siteSection: 'booking engine',
            template: 'cbe',
          },
          booking: {
            currency: 'USD',
          },
          ...userParams,
        }
      },
    getBookingTrackingV2:
      (state, getters, rootState, rootGetters) => (query = state.query, eventData) => {
        const userProfile = rootState.auth.user
        const { isBookingEditMode } = rootState.modifyReservation

        if (!isBookingEditMode) {
          let userParams = {}
          if (userProfile) {
            userParams = {
              user: {
                accountType: rootGetters['auth/fanLevel'],
                accountNumber: rootGetters['auth/userId']
                  ? rootGetters['auth/userId'].toLowerCase()
                  : '',
                loginState: 'logged in',
                ireckonuID: userProfile?.personId?.toString() || null
              },
            }
          }
          return {
            ...eventData,
            page: {
              hotelCode: query.hotelCode || 'global',
              siteSection: 'booking engine',
              template: 'cbe',
            },
            ...userParams,
          }
        }
      },
    getCurrentRoomId: (state) => state.currentRoomId,
    areDatesAndGuestsEmpty: (state) => !state.query.stayDateStart || !state.query.stayDateEnd || !state.query.adultGuestCount,
    isMultiRoomReservation: (state) => (customQuery = null) => {
      const query = customQuery ?? state.query
      return Number(query.roomQuantity) > 1
    },
    bookingFlow: (state, getters, rootState) => (customQuery = null) => {
      const editModeFlow = getters.isMultiRoomReservation(customQuery) ? 'multiRoomMod' : 'singleRoomMod'
      const defaultFlow = getters.isMultiRoomReservation(customQuery) ? 'multiRoom' : 'singleRoom'
      return rootState.modifyReservation.isBookingEditMode ? editModeFlow : defaultFlow
    }
  },
  /** Mutations */
  mutations: {
    COMPONENT_CONFIGURED(/** @type {State} */ state) {
      state.configured = true
    },
    DATA_READY(/** @type {State} */ state) {
      state.ready = true
    },
    REGISTER_ROUTER(_, { vueRouter, vueRoute }) {
      if (!router) {
        router = vueRouter
      }
      if (!route) {
        route = vueRoute
      }
    },
    UPDATE_FULL_QUERY(/** @type {State} */ state, query) {
      Object.keys(state.query)
        .filter((prop) => typeof query[prop] !== 'undefined')
        .forEach((prop) => {
          updateQueryProp(prop, query[prop], state.query, query)
        })
      routerQuery(state.query)
    },
    UPDATE_QUERY(/** @type {State} */ state, { prop, value }) {
      const existingProps = Object.keys(state.query)
      if (existingProps.includes(prop)) {
        updateQueryProp(prop, value, state.query)
      }
      routerQuery(state.query)
    },
    UPDATE_STEP(/** @type {State} */ state, step) {
      state.step = step
    },
    UPDATE_STEP_BUTTON(
      /** @type {State} */
      state,
      { label, step, customClass = '' }
    ) {
      state.nextStepLabel = label
      state.nextStepIndex = step
      state.nextStepClass = customClass
    },
    UPDATE_CALENDAR_DATES(
      /** @type {State} */
      state,
      {
        dates, hotelCode, month
      }
    ) {
      // Ensure the hotelCode exists in state.calendarDates
      if (!state.calendarDates[hotelCode]) {
        this._vm.$set(state.calendarDates, hotelCode, {})
      }

      // Ensure the month exists within the hotelCode
      if (!state.calendarDates[hotelCode][month]) {
        this._vm.$set(state.calendarDates[hotelCode], month, [])
      }

      // Update the calendarDates for the specific hotelCode and month
      this._vm.$set(state.calendarDates[hotelCode], month, [...dates])
    },
    CLEAR_CALENDAR_DATES(
      /** @type {State} */
      state
    ) {
      state.calendarDates = {}
    },
    CALENDAR_QUERY_LOADING(/** @type {State} */ state, loading) {
      state.calendarLoading = loading
    },
    SELECT_ROOM(/** @type {State} */ state, index) {
      state.currentRoomId = index
    },
    CHECK_OUT_PENDING(/** @type {State} */ state, pending = false) {
      state.checkOutPending = pending
    },
    UPDATE_RANGE_TOTAL(/** @type {State} */ state, rangeTotal) {
      state.rangeTotal = rangeTotal
    },
  },
  /** Booking Actions
   * @typedef {Object} Actions
   * @property {ActionFunction<undefined,void>} componentConfigured - Set configured as true
   * @property {ActionFunction<undefined,void>} clearData - Return {@link QueryOptions} and {@link State.step} to it's original states
   * @property {ActionFunction<{vueRouter: VueRouter, vueRoute: VueRoute},void>} registerRouter - Register VueRoter and Route to it's declared variables
   * @property {ActionFunction<VueRoute,void>} updateRoute - Update Rote object
   * @property {ActionFunction<{prop: string, value: any, query: QueryOptions},void>} updateQuery - Update {@link QueryOptions} by replacing all query or changing a single value
   * @property {ActionFunction<{label: string, step: number, customClass: string},void>} updateNextStepButton - Update footer button
   * @property {ActionFunction<'push'|'replace',void>} nextStep - Move for the next step of the wizard
   * @property {ActionFunction<number,void>} prevStep - Move for the choosen step of the wizard
   * @property {ActionFunction<number,void>} selectRoom - Choose the editing room
   * @property {ActionFunction<undefined,void>} validateStep - Validate step index based on query parameters + route name
   * @property {ActionFunction<undefined,void>} validateRoute - Validate criterias to allow routing to deternimed routes
   * @property {ActionFunction<{startDate: Date, endDate: Date, month: string, customQuery: QueryOptions},void>} getCalendarMonthData - Get [DateRangeInfos]{@link DateRangeInfo} from a certain month based on the {@link QueryOptions}
   * @property {ActionFunction<CheckOutRequest,void>} checkOut - Post checkout form
   */
  /** @type {Actions} */
  actions: {
    componentConfigured({ commit }) {
      commit('COMPONENT_CONFIGURED')
    },
    async clearData({ dispatch, commit }) {
      await dispatch('updateQuery', {
        query: {
          ...defaultQuery,
        },
      })
      commit('UPDATE_STEP', 0)
    },
    registerRouter({ commit }, { vueRouter, vueRoute }) {
      commit('REGISTER_ROUTER', { vueRouter, vueRoute })
    },
    updateRoute(_, vueRoute) {
      route = vueRoute
    },
    updateQuery({ commit }, { prop, value, query }) {
      if (query) {
        commit('UPDATE_FULL_QUERY', query)
      } else {
        commit('UPDATE_QUERY', { prop, value })
      }
    },
    updateNextStepButton({ commit }, { label, step, customClass }) {
      commit('UPDATE_STEP_BUTTON', { label, step, customClass })
    },
    nextStep({ state, commit }, method = 'push') {
      if (state.step === steps.length - 1) {
        return
      }
      commit('UPDATE_STEP', state.step + 1)
      routerPush(genenerateRouteParams(state.step), method)
    },
    prevStep({ state, commit }, method = 'push') {
      if (state.step === 0) {
        return
      }
      commit('UPDATE_STEP', state.step - 1)
      routerPush(genenerateRouteParams(state.step), method)
    },
    goToStep({ commit, state }, step) {
      if (step < 0 || step > steps.length - 1) {
        return
      }
      if (typeof step === 'string') {
        const stepIndex = steps.indexOf(step)
        if (stepIndex >= 0) {
          commit('UPDATE_STEP', stepIndex)
        }
      } else if (typeof step === 'number') {
        commit('UPDATE_STEP', step)
      }
      routerPush(genenerateRouteParams(state.step))
    },
    updateStep({ commit, state }, step) {
      if (step < 0 || step > steps.length - 1) {
        return
      }
      if (typeof step === 'string') {
        const stepIndex = steps.indexOf(step)
        if (stepIndex >= 0) {
          commit('UPDATE_STEP', stepIndex)
        }
      } else if (typeof step === 'number') {
        commit('UPDATE_STEP', step)
      }
    },
    selectRoom({ commit, dispatch }, roomIndex = 0) {
      commit('SELECT_ROOM', roomIndex)
      dispatch('rooms/reorderRoomsBySelection', null, { root: true })
    },
    async validateStep({ state, commit }) {
      const { step } = route.query
      const stepIndex = steps.indexOf(route.name)
      if (step && state.step !== step) {
        commit('UPDATE_STEP', parseInt(step))
      } else if (stepIndex > -1) {
        commit('UPDATE_STEP', stepIndex)
      }
    },
    async validateRoute(
      {
        state, commit, getters, dispatch, rootState, rootGetters
      },
      options
    ) {
      const isFirstLoad = !state.ready
      const isValid = await dispatch('rooms/validateRequiredParams', null, {
        root: true,
      })
      if (!rootState.cart.cart) {
        logger.debug('get cart')
        await dispatch('cart/getCart', null, { root: true })
      }
      if (isFirstLoad) {
        commit('DATA_READY')
      }
      const isMultiroom = state.query.roomQuantity > 1
      // If no rooms available and step 0 we get new rooms
      if (
        rootGetters['rooms/roomsAvailable'].length === 0
        && isValid
        && state.step === 0
      ) {
        logger.debug('no rooms')
        if (!rootGetters['room/loadingRooms']) {
          await dispatch('rooms/getAvailableRooms', null, { root: true })
        }
      }
      // Else if all rooms are successfully added to the cart
      else if (
        state.step === 0
        && rootGetters['cart/allRoomsAdded']
        && !isFirstLoad
        && !options?.roomsAvailOnly
      ) {
        logger.debug('all rooms in cart')
        const hasIataNumber = state.query.iataNumber !== null
        // Multi room flow takes the user for an overview page before moving forward
        const isOverviewPage = route.name === 'rooms-overview'
        if (isMultiroom && !isOverviewPage) {
          if (options?.isAllSameRooms || getters.currentRoomIsLastRoom) {
            const params = genenerateRouteParams(state.step)
            params.name = 'rooms-overview'
            routerPush(params)
          } else {
            await dispatch('selectRoom', state.currentRoomId + 1)
            routerPush(genenerateRouteParams(0))
          }
        } else if (hasIataNumber) {
          await dispatch('goToStep', 'check-out')
        } else {
          const userProfile = rootState.auth.user
          if (userProfile) {
            await dispatch('goToStep', 'enhancements')
            await dispatch('selectRoom', 0)
          } else {
            await dispatch('nextStep')
            await dispatch('selectRoom', 0)
          }
        }
      } else if (
        // Else if not all rooms have been added to the cart
        state.step === 0
        && !rootGetters['cart/allRoomsAdded']
        && !options?.roomsAvailOnly && !getters.areDatesAndGuestsEmpty
      ) {
        logger.debug('not all rooms in cart')
        await dispatch('selectRoom', state.currentRoomId + 1)
        routerPush(genenerateRouteParams(0))
      }
    },
    async getCalendarMonthData(
      { commit, state },
      {
        calendarType,
        startDate = null,
        endDate = null,
        month = null,
        date = null,
        customQuery,
      } // parameters for scroll and pagination now together to simplify things
    ) {
      if (state.calendarLoading) {
        throw new Error('dates already loading')
      }
      const query = customQuery ?? state.query
      if (!query.hotelCode) {
        throw new Error('no hotel selected')
      }

      commit(
        'CALENDAR_QUERY_LOADING',
        calendarType === 'scroll' ? date : month
      ) // hybrid of scroll and paginations
      let roomCodes = [...query.roomCodes.map((c) => c.trim())]
      if (query.bedType) {
        roomCodes = [query.bedType.trim()]
      }
      const rateCode = query.groupCode || query.rateCode
      let data = null
      try {
        if (calendarType === 'scroll') {
          const monthNumber = date.split('-')[0] // use for calendar-scroll
          const year = date.split('-')[1] // use for calendar-scroll
          await axios
            .get(getApiUrl('/api/v1/synxis/get-calendar-month-year'), {
              // use this for calendar-scroll
              params: {
                monthNumber, // use for calendar-scroll
                year, // use for calendar-scroll
                pid: query.hotelCode,
                roomCodes: roomCodes.length > 0 ? roomCodes.join(',') : null,
                rateCode: rateCode || null,
                promotionCode: query.promotionCode ? query.promotionCode : null,
                adultGuestCount: query.adultGuestCount,
                childGuestCount: query.childGuestCount,
              },
            })
            .then((res) => {
              data = res.data
            })
        } else {
          // Pagingation calendar
          await axios
            .get(getApiUrl('/api/v1/synxis/get-calendar'), {
              // use this for calendar-arrow
              params: {
                startDate, // use for calendar-arrow
                endDate, // use for calendar-arrow
                pid: query.hotelCode,
                roomCodes: roomCodes.length > 0 ? roomCodes.join(',') : null,
                rateCode: rateCode || null,
                promotionCode: query.promotionCode ? query.promotionCode : null,
                adultGuestCount: query.adultGuestCount,
                childGuestCount: query.childGuestCount,
              },
            })
            .then((res) => {
              data = res.data
            })
        }
        const roomCodesMap = roomCodes.length > 0
          ? {
            roomCodes: roomCodes.join(','),
          }
          : {}
        const rateCodeMap = query.rateCode
          ? {
            rateCode: query.rateCode,
          }
          : {}
        const promotionCodeMap = query.promotionCode
          ? {
            promotionCode: query.promotionCode,
          }
          : {}

        // organizar ranges por Hotel->mes->ranges

        const daysRange = data.daysRange.map(
          (/** @type {DateRangeInfo} */ r) => ({
            ...r,
            ...roomCodesMap,
            ...rateCodeMap,
            ...promotionCodeMap,
          })
        )
        commit('UPDATE_CALENDAR_DATES', {
          dates: daysRange,
          hotelCode: query.hotelCode,
          month: calendarType === 'scroll' ? date : month
        })
        commit('CALENDAR_QUERY_LOADING', null)
      } catch (err) {
        commit('CALENDAR_QUERY_LOADING', null)
        throw new Error(err)
      }
    },
    async transformCheckOutData({ commit, rootState, rootGetters }, data) {
      let checkOutData = {}
      checkOutData.isDataFromVGS = false
      if (rootState.modifyReservation.isBookingEditMode) {
        const confirmationsIds = rootState.modifyReservation.selectedReservations.map(
          (reservation) => reservation.id
        )
        const { emailReservation } = rootState.modifyReservation.currentReservationData
        const guestProfile = rootState.modifyReservation.selectedReservations[0].guests[0].profiles
          .profileInfo
        const additionalGuests = []
        const guestCount = Object.keys(data).filter((key) => key.startsWith('additional-guest'))
          .length / 3

        if (guestCount) {
          for (let i = 1; i <= guestCount; i++) {
            const guest = {
              FirstName: data[`additional-guest-firstname-${i}`] || '',
              LastName: data[`additional-guest-lastname-${i}`] || '',
              ConfirmationNumber:
                data[`additional-guest-confirmation-${i}`] || '',
            }
            if (guest.FirstName.trim() !== '' || guest.LastName.trim() !== '') {
              additionalGuests.push(guest)
            }
          }
          // remove all the additional guest individual fields once in additionalGuests
          for (const key in data) {
            if (key.startsWith('additional-guest')) {
              delete data[key]
            }
          }
        }
        checkOutData.confirmations = confirmationsIds
        checkOutData.email = emailReservation
        checkOutData.baseCheckOutInfo = {
          ...data,
          guestMembershipInfo: {
            custLoyalty: guestProfile.profile.customer.custLoyalty,
            uniqueID: guestProfile.uniqueID[0],
          },

          cartModel: rootState.cart.cart,
          userAgent: navigator.userAgent,
          lang: rootState.global.lang,
          additionalGuests,
        }
      } else {
        checkOutData = data
        checkOutData.cartModel = rootState.cart.cart
        checkOutData.userAgent = navigator.userAgent
        checkOutData.lang = rootState.global.lang

        const userProfile = rootState.auth.user
        if (userProfile) {
          checkOutData.profileId = userProfile?.profileID ?? ''
          checkOutData.guestMembershipInfo = {
            custLoyalty: {
              membershipID: rootGetters['auth/userId'],
              loyalLevel: rootGetters['auth/fanLevel']
            }
          }
        }
      }
      commit('CHECK_OUT_PENDING', true)
      return checkOutData
    },
    async checkOut({ commit, rootState }, checkOutData) {
      const checkoutApi = rootState.modifyReservation.isBookingEditMode
        ? '/api/v1/booking/ModifyReservation'
        : '/api/v1/booking/check-out'

      let result = null

      try {
        if (!checkOutData.isDataFromVGS) {
          const response = await axios.post(getApiUrl(checkoutApi), checkOutData)
          result = response.data
        } else {
          if (checkOutData.error) {
            throw new Error(checkOutData)
          }
          result = checkOutData
        }

        // Run only for SBE, storedReservationLookUpData Email gets updated before redirecting to SBE detail page
        const storedReservationLookUpDataLocalSession = sessionStorage.getItem(
          'storedReservationLookUpData'
        )

        if (
          rootState.modifyReservation.isBookingEditMode
          && storedReservationLookUpDataLocalSession
          && result
        ) {
          let storedReservationLookUpDataStr = storedReservationLookUpDataLocalSession
          const lookUpData = JSON.parse(storedReservationLookUpDataStr)
          if (checkOutData.baseCheckOutInfo) { // Replace this with the new email
            lookUpData.Email = checkOutData.baseCheckOutInfo.email
          } else {
            lookUpData.Email = checkOutData.email
          }
          storedReservationLookUpDataStr = JSON.stringify(lookUpData)
          sessionStorage.setItem(
            'storedReservationLookUpData',
            storedReservationLookUpDataStr
          )
        }

        commit('CHECK_OUT_PENDING', false)
        return result
      } catch (err) {
        commit('CHECK_OUT_PENDING', false)
        throw err.response.data
      }
    },
    updateRangeTotal({
      state, getters, commit, rootState
    }, customQuery) {
      const query = customQuery ?? state.query
      const duration = getters.getDateRangeInDays(query)
      if (duration === 0) {
        commit('UPDATE_RANGE_TOTAL', 0.0)
        return 0
      }
      const startDate = getDate(query.stayDateStart, dayFormat)
      if (!getters.getDateRangeInfo(startDate, query)) {
        commit('UPDATE_RANGE_TOTAL', 0.0)
        return 0
      }
      const daysPrices = []
      for (let i = 0; i < duration; i++) {
        const currentDay = startDate.clone().add(i, 'day')
        const dayInfo = getters.getDateRangeInfo(currentDay, query)
        if (dayInfo) {
          const dayPrice = getters.showPricesWithTaxes(query) ? parseFloat(dayInfo.priceWithTaxes) : parseFloat(dayInfo.price)
          daysPrices.push(dayPrice)
        } else {
          daysPrices.push(0)
        }
      }
      const rangeTotal = daysPrices.reduce((a, b) => a + b, 0) || 0.0
      commit('UPDATE_RANGE_TOTAL', rangeTotal)
      return rangeTotal
    },
    clearAllCalendarDates({ commit }) {
      commit('CLEAR_CALENDAR_DATES')
    },
  }
}
